import { useMemo } from 'react';
import type { AdminConversation, ListConversationsPagination } from '../../api/types.js';
import type { ConversationScope } from '../../hooks/useConversations.js';
import { displayName, formatTime, highlight, modeText, renderWechatEmojis } from '../../lib/utils.js';
import { Avatar } from '../ui/Avatar.js';
import { BotAccountBadge } from '../ui/BotAccountBadge.js';

export interface ConversationListProps {
  conversations: AdminConversation[];
  activeFriendId: number | null;
  loading: boolean;
  searchTerm: string;
  debouncedSearchTerm: string;
  scope: ConversationScope;
  pagination: ListConversationsPagination;
  onSearchChange: (value: string) => void;
  onScopeChange: (value: ConversationScope) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onSelect: (friendId: number) => void;
  onRefresh: () => void;
  statText: string;
}

interface ConversationGroup {
  title: string;
  items: AdminConversation[];
}

export function ConversationList({
  conversations,
  activeFriendId,
  loading,
  searchTerm,
  debouncedSearchTerm,
  scope,
  pagination,
  onSearchChange,
  onScopeChange,
  onPreviousPage,
  onNextPage,
  onSelect,
  onRefresh,
  statText,
}: ConversationListProps) {
  const effectiveSearchTerm = debouncedSearchTerm || searchTerm.trim();
  const groups = useMemo(() => groupConversations(conversations, effectiveSearchTerm), [conversations, effectiveSearchTerm]);
  const hasData = conversations.length > 0;
  const hasSearch = effectiveSearchTerm.length > 0;
  const searchStatus = getSearchStatus({ hasSearch, loading, count: conversations.length });

  return (
    <>
      <header className="sidebar-header">
        <div>
          <h1>客服后台</h1>
          <div className="sidebar-stat">{statText}</div>
        </div>
        <div className="header-actions">
          <a className="button ghost" href="/admin/debug-chat.html">
            调试框
          </a>
          <button className="button ghost" type="button" title="刷新" onClick={onRefresh}>
            刷新
          </button>
        </div>
      </header>

      <section className="filter-panel">
        <input
          className="search-input"
          placeholder="搜索客户、消息内容..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {searchStatus ? <div className="search-status">{searchStatus}</div> : null}
        <div className="segmented" role="group" aria-label="会话范围">
          <button
            className={`segment ${scope === 'whitelist' ? 'active' : ''}`}
            type="button"
            onClick={() => onScopeChange('whitelist')}
          >
            白名单
          </button>
          <button
            className={`segment ${scope === 'all' ? 'active' : ''}`}
            type="button"
            onClick={() => onScopeChange('all')}
          >
            全部
          </button>
          <button
            className={`segment ${scope === 'manual' ? 'active' : ''}`}
            type="button"
            onClick={() => onScopeChange('manual')}
          >
            已转人工
          </button>
        </div>
      </section>

      <div className="conversation-list">
        {loading && !hasData ? (
          <div className="empty-state small">
            <div className="empty-illustration">...</div>
            <strong>{hasSearch ? '搜索中...' : '加载会话中'}</strong>
          </div>
        ) : !hasData ? (
          <div className="empty-state small">
            <div className="empty-illustration">暖</div>
            <strong>
              {hasSearch
                ? '没有找到匹配的客户'
                : scope === 'whitelist'
                  ? '白名单暂无会话'
                  : scope === 'manual'
                    ? '最近 7 天暂无已转人工会话'
                    : '最近 7 天暂无会话'}
            </strong>
            {hasSearch ? <p>销售账号只能搜索已绑定工作号</p> : null}
            {hasSearch ? (
              <button className="button" type="button" onClick={() => onSearchChange('')}>
                清除筛选
              </button>
            ) : null}
          </div>
        ) : (
          groups.map((group) =>
            group.items.length === 0 ? null : (
              <div key={group.title}>
                <div className="conversation-group">{group.title}</div>
                {group.items.map((item) => (
                  <button
                    key={item.friendId}
                    className={`conversation-item ${item.friendId === activeFriendId ? 'active' : ''}`}
                    type="button"
                    onClick={() => onSelect(item.friendId)}
                  >
                    <Avatar name={displayName(item)} size="md" />
                    <div className="conversation-main">
                      <div className="conversation-top">
                        <div className="conversation-identity">
                          <div
                            className="conversation-name"
                            dangerouslySetInnerHTML={{ __html: highlight(displayName(item), effectiveSearchTerm) }}
                          />
                          <BotAccountBadge
                            botWechatRecordId={item.botWechatRecordId}
                            botNickname={item.botNickname}
                          />
                        </div>
                        {item.unreadCount > 0 ? (
                          <span className="badge">{item.unreadCount}</span>
                        ) : (
                          <span className="conversation-time">{formatTime(item.lastMessageAt)}</span>
                        )}
                      </div>
                      <div
                        className="conversation-last"
                        dangerouslySetInnerHTML={{
                          __html: renderWechatEmojis(highlight(item.lastMessage || '', effectiveSearchTerm)),
                        }}
                      />
                      <div className="conversation-meta">
                        <div className="tag-row">
                          {item.isWhitelisted ? <span className="tag whitelist">白名单</span> : null}
                          <span className={`tag ${item.mode === 'manual' ? 'manual' : ''}`}>{modeText(item.mode)}</span>
                        </div>
                        {item.unreadCount > 0 ? (
                          <span className="conversation-time">{formatTime(item.lastMessageAt)}</span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ),
          )
        )}
      </div>

      <footer className="conversation-pagination">
        <button
          className="button ghost"
          type="button"
          disabled={!pagination.hasPrevious || loading}
          onClick={onPreviousPage}
        >
          上一页
        </button>
        <span>{formatPaginationLabel(pagination, loading)}</span>
        <button
          className="button ghost"
          type="button"
          disabled={!pagination.hasNext || loading}
          onClick={onNextPage}
        >
          下一页
        </button>
      </footer>
    </>
  );
}

function formatPaginationLabel(pagination: ListConversationsPagination, loading: boolean): string {
  if (loading) {
    return '加载中...';
  }
  if (pagination.count === 0) {
    return '暂无会话';
  }
  const start = pagination.offset + 1;
  const end = pagination.offset + pagination.count;
  return `${start}-${end}`;
}

function groupConversations(conversations: AdminConversation[], searchTerm: string): ConversationGroup[] {
  if (searchTerm) {
    return [{ title: '匹配结果', items: conversations }];
  }

  const active: AdminConversation[] = [];
  const archived: AdminConversation[] = [];
  const now = Date.now();

  for (const item of conversations) {
    const lastAt = item.lastMessageAt ? new Date(item.lastMessageAt).getTime() : 0;
    if (lastAt && now - lastAt <= 24 * 60 * 60 * 1000) {
      active.push(item);
    } else {
      archived.push(item);
    }
  }

  return [
    { title: '24 小时活跃', items: active },
    { title: '最近 7 天', items: archived },
  ];
}

function getSearchStatus({
  hasSearch,
  loading,
  count,
}: {
  hasSearch: boolean;
  loading: boolean;
  count: number;
}): string | null {
  if (!hasSearch) {
    return null;
  }
  if (loading) {
    return '搜索中...';
  }
  return count > 0 ? `找到 ${count} 个匹配` : '没有匹配结果';
}
